import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Building2, User } from "lucide-react";
import { useState } from "react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChannelBadge } from "./ChannelBadge";
import { fetchLeadboxerLeads } from "./leadboxer/api";
import { VISITOR_INSIGHT_PERIODS } from "./leadboxer/instances";
import type { EnrichedLead, LeadboxerInstance } from "./leadboxer/types";
import {
  getLastActivityTimestamp,
  getLeadDisplayName,
  getLeadEvents,
  getLeadVisits,
} from "./leadboxer/utils";
import { VisitorLeadModal } from "./VisitorLeadModal";

const isNonEnglishRelativeTime = (value: string) =>
  /[åäöÅÄÖ]|\b(minuutti|tunti|päivä|sekunti|vuorokausi|sitten|sedan|minutter|timer|dage|sekunder|dagar)\b/i.test(
    value,
  );

function formatLastActivity(lead: EnrichedLead): string {
  const pretty = lead.prettyLastEvent;
  if (pretty && typeof pretty === "string" && isNonEnglishRelativeTime(pretty)) {
    const ts = getLastActivityTimestamp(lead);
    if (ts) {
      return formatDistanceToNow(new Date(ts), { addSuffix: true });
    }
  }
  return (pretty as string) || "-";
}

export const VisitorLeadsTable = ({
  instance,
}: {
  instance: LeadboxerInstance;
}) => {
  const translate = useTranslate();
  const [period, setPeriod] = useState(7);
  const [selectedLead, setSelectedLead] = useState<EnrichedLead | null>(null);

  const { data: leads = [], isPending, error } = useQuery({
    queryKey: ["visitor-insights", "leads", instance, period],
    queryFn: () => fetchLeadboxerLeads(instance, period),
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {VISITOR_INSIGHT_PERIODS.map((p) => (
          <Button
            key={p.value}
            type="button"
            size="sm"
            variant={period === p.value ? "default" : "outline"}
            onClick={() => setPeriod(p.value)}
          >
            {translate(p.labelKey, { _: p.fallback })}
          </Button>
        ))}
      </div>

      {error ? (
        <p className="text-destructive text-sm mb-4">
          {error instanceof Error ? error.message : "Failed to load visitors"}
        </p>
      ) : null}

      {isPending ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Spinner className="size-4" />
          <span>{translate("crm.visitor_insights.loading", { _: "Loading..." })}</span>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {translate("crm.visitor_insights.columns.summary", {
                    _: "Summary",
                  })}
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  {translate("crm.visitor_insights.columns.last_activity", {
                    _: "Last Activity",
                  })}
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  {translate("crm.visitor_insights.columns.industry", {
                    _: "Industry",
                  })}
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  {translate("crm.visitor_insights.columns.employees", {
                    _: "Employees",
                  })}
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  {translate("crm.visitor_insights.columns.visits", {
                    _: "Visits",
                  })}
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  {translate("crm.visitor_insights.columns.events", {
                    _: "Events",
                  })}
                </TableHead>
                <TableHead>
                  {translate("crm.visitor_insights.columns.last_channel", {
                    _: "Last Channel",
                  })}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    {translate("crm.visitor_insights.empty", {
                      _: "No visitors found for this period.",
                    })}
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead, idx) => {
                  const name = getLeadDisplayName(lead);
                  const logo = lead.organizationLogoUrl || lead.leadLogoUrl;
                  const lastActivity = formatLastActivity(lead);
                  const industry = lead.organizationIndustryName || "-";
                  const employees =
                    lead.organizationEmployeeCountRangeName || "-";
                  const visits = getLeadVisits(lead);
                  const events = getLeadEvents(lead);
                  const lastChannel = (lead.last_channel || "-") as string;

                  return (
                    <TableRow
                      key={String(lead.machine_id ?? idx)}
                      className="cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {logo ? (
                            <img
                              src={String(logo)}
                              alt=""
                              className="size-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="size-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="size-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{name}</div>
                            {lead.crmCompany ? (
                              <Link
                                to={`/companies/${lead.crmCompany.id}`}
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Building2 className="size-3" />
                                {lead.crmCompany.name}
                              </Link>
                            ) : (
                              <div className="text-xs text-muted-foreground">
                                {lead.organizationCity || ""}
                              </div>
                            )}
                            <div className="md:hidden text-xs text-muted-foreground mt-1">
                              {lastActivity} • {visits} visits • {events} events
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {lastActivity}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                        {industry}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                        {employees}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {visits}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {events}
                      </TableCell>
                      <TableCell>
                        <ChannelBadge channel={lastChannel} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <VisitorLeadModal
        lead={selectedLead}
        instance={instance}
        open={!!selectedLead}
        onOpenChange={(open) => {
          if (!open) setSelectedLead(null);
        }}
      />
    </div>
  );
};
